import styles from "shared/ContentList/ContentList.module.css";

import Link from "next/link";
import { TimeRelative } from "src/components/time/time";
import { Icon } from "src/components/icon/icon";
import Stars from "./QueryStar";
import { Avatar } from "src/components/avatar/avatar";

import useQueriesFavorites, { Output } from "../api/useQueriesFavorites";

import type { QueryType } from "../api/queryItem";
import { QueryScheduleBadge } from "shared/ScheduleBadge/QueryScheduleBadge";
import { useHasViewPermission } from "src/utils/permissions/permissions";
import { Badge } from "src/components/ui-components/Badge/Badge";
import { IconTableMaterialized } from "src/components/ui-components/Icons/IconTableMaterialized";
import { Tags } from "src/components/tags/tags";
import { ThreeDotsMenu } from "src/components/page-components/Library/MoveContentIndividually/ThreeDotsMenu";
import { useWindowSize } from "@reach/window-size";
import { withNoSSR } from "src/components/ui-components/withNoSSR/withNoSSR";

interface QueryProps {
  query: QueryType;
  favorites: Output;
  origin?: "creations";
  refetchFolderContent?: () => Promise<void> | void;
  trackResultClicks?: () => Promise<void> | void;
}

export const QueryTableRow = withNoSSR(
  ({
    query,
    favorites,
    origin,
    refetchFolderContent,
    trackResultClicks,
  }: QueryProps) => {
    const hasViewPermission = useHasViewPermission(query.owner);
    const isMobile = useWindowSize().width < 699;

    const nameAndTags = (
      <div className={styles.nameAndTags}>
        <Link
          href={`/queries/${query.id}`}
          className={styles.contentName}
          onClick={trackResultClicks}
        >
          {query.name}
        </Link>
        {query.tags.length > 0 && (
          <Tags
            className={styles.tags}
            slug="queries"
            tags={query.tags.slice(0, 5)}
          />
        )}
      </div>
    );

    const avatarOrIcon =
      origin === "creations" ? (
        <Link href={`/queries/${query.id}`}>
          <Icon icon="terminal" />
        </Link>
      ) : (
        <Link href={`/${query.owner.handle}`}>
          <Avatar
            src={query.owner.profile_image_url}
            ownerId={query.owner.id}
            handle={query.owner.handle}
            type={query.owner.type}
            size={isMobile ? 16 : 25}
          />
        </Link>
      );

    const rightContainer = (
      <div className={styles.rightContainer}>
        <div className={styles.badges}>
          {Boolean(query.matview_id) && (
            <div className={styles.matViewBadge}>
              <Badge size="L" color="info" variant="filled">
                Materialized
                <IconTableMaterialized />
              </Badge>
            </div>
          )}
          {hasViewPermission && (
            <QueryScheduleBadge size="L" queryId={query.id} />
          )}
        </div>
        <div className={styles.actions}>
          <Stars
            query_id={query.id}
            stars={query.favoriteCount}
            isFavorite={favorites.dict?.[query.id]?.iHaveFavorited ?? false}
          />
          <ThreeDotsMenu
            contentItem={{
              type: "query",
              id: query.id,
              owner: query.owner,
            }}
            refetchFolderContent={refetchFolderContent}
          />
        </div>
      </div>
    );

    if (isMobile) {
      return (
        <tr>
          <td>
            <div className={styles.multiline}>
              {nameAndTags}
              <div className={styles.ownerAndDetails}>
                {avatarOrIcon}
                <Link href={`/${query.owner.handle}`}>
                  @{query.owner.handle}
                </Link>
              </div>
              <div className={styles.ownerAndDetails}>
                <span>
                  updated <TimeRelative>{query.updated_at}</TimeRelative>
                </span>
                {query.is_private && <Icon icon="lock-fill" />}
              </div>
            </div>
          </td>
          <td>{rightContainer}</td>
        </tr>
      );
    }

    return (
      <tr>
        <td className={styles.avatarColumn}>{avatarOrIcon}</td>
        <td>
          <div className={styles.multiline}>
            {nameAndTags}
            <div className={styles.ownerAndDetails}>
              <Link href={`/${query.owner.handle}`}>@{query.owner.handle}</Link>
              <span>
                • updated <TimeRelative>{query.updated_at}</TimeRelative>
              </span>
              {query.is_private && <Icon icon="lock-fill" />}
            </div>
          </div>
        </td>
        <td>{rightContainer}</td>
      </tr>
    );
  }
);

interface Props {
  queries: QueryType[];
  origin?: "creations";
  trackResultClicks?: (
    properties: Record<string, string | number>
  ) => Promise<void> | void;
}

export default function QueriesList(props: Props) {
  const queryIds = props.queries.map((q) => q.id);
  const favorites = useQueriesFavorites(queryIds);

  const trackClicks = (query: QueryType, idx: number) => {
    const properties = {
      result_position: idx,
      query_id: query.id,
      query_name: query.name,
      query_owner: query.owner.handle,
    };

    return () => {
      if (props.trackResultClicks) {
        props.trackResultClicks(properties);
      }
    };
  };

  return (
    <table className={styles.table}>
      <tbody>
        {props.queries.map((query, idx) => (
          <QueryTableRow
            key={query.id}
            query={query}
            favorites={favorites}
            origin={props.origin}
            trackResultClicks={trackClicks(query, idx)}
          />
        ))}
      </tbody>
    </table>
  );
}
